import {useSuspenseInfiniteQuery, useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {type ApiResponseGetMonthlyDrivingInfoResponse, type DailyTripHistory} from '@di-shared/api/di/__generated';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {FuelType, TripOrderByType} from '@di-shared/api/di/type';
import {getYYYYMMddFormat, getYYYYMMFormat} from '@di-shared/api/di/utils';
import {VALID_MONTH_PERIOD} from '@di-shared/features/history/driving-info/useDrivingInfo.kr';

export default function useTripDetailKr({
  vin,
  selectedDate,
  orderBy = TripOrderByType.DESC,
}: {
  vin: string;
  selectedDate: Date;
  orderBy?: TripOrderByType;
}) {
  const ccId = environmentStore.getState().ccid;
  const today = new Date();

  const drivingParams = {
    vin,
    ccId,
    startDate: getYYYYMMFormat(subDate(today, {months: VALID_MONTH_PERIOD - 1})),
    endDate: getYYYYMMFormat(today),
  };

  const {data: drivingData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.fuel.getMonthlyDrivingInfo), drivingParams],
    queryFn: () => diApi.fuel.getMonthlyDrivingInfo(drivingParams),
    select: ({data}: ApiResponseGetMonthlyDrivingInfoResponse) => data,
  });

  const verifiedDiAgreeDate = parseDateWithFormat(drivingData.ccsAgreeDate);
  if (!verifiedDiAgreeDate) {
    throw new Error('useTripDetailKr diAgreeDate parse error');
  }
  const isEv = drivingData.fuelType === FuelType.E;

  const params = {
    vin,
    ccId,
    date: getYYYYMMddFormat(selectedDate),
    size: 3,
    orderBy,
  };

  const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useSuspenseInfiniteQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getTripHistoryByDate), params],
    queryFn: async ({pageParam = 1}) => {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      return diApi.driveHistory.getTripHistoryByDate({page: pageParam, ...params});
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPage, lastPageParam) => {
      return lastPage.data.pageNumber < lastPage.data.totalPages ? lastPageParam + 1 : undefined;
    },
  });

  const isEmptyTrip = data.pages[0].data.content.length === 0;
  const summaryData = data.pages[0].data.summary;
  const totalDrivingHours = Math.floor(summaryData.totalDrvTime / 60);
  const totalDrivingMinutes = summaryData.totalDrvTime % 60;

  const tripDetails = data.pages.flatMap((trip) => trip.data.content);
  const formattedTripDetails = tripDetails.map((trip) => ({
    ...trip,
    intervalIgnitionHours: Math.floor(trip.drvTime / 60),
    intervalIgnitionMinutes: trip.drvTime % 60,
    isDrivingLessThanOneMinute: trip.drvTime === 0,
  }));

  const formattedRecentTripDetail: DailyTripHistory & {
    intervalIgnitionHours: number;
    intervalIgnitionMinutes: number;
    isDrivingLessThanOneMinute: boolean;
  } = formattedTripDetails[0];

  return {
    drivingData,
    isEv,
    isEmptyTrip,
    summaryData,
    totalDrivingHours,
    totalDrivingMinutes,
    formattedTripDetails,
    formattedRecentTripDetail,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
